// src/components/orders/OrderPaymentModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Modal, Form, InputNumber, Select, DatePicker, Input,
    Descriptions, Tag, Divider, message, Spin, Alert, Segmented
} from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { addOrderPayment, getOrderPayments } from '../../services/pedidosService';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/es_ES';

const { Option } = Select;
const { TextArea } = Input;

/**
 * OrderPaymentModal — Register a payment (partial, full, on-delivery) for a purchase order.
 *
 * Props:
 *   visible  {boolean}   — Show/hide the modal
 *   order    {object}    — Order row from the table (must include id, order_number, total, paid_amount, pending_amount, payment_status)
 *   onClose  {function}  — Called when modal closes
 *   onPaymentRegistered {function} — Called after a successful payment registration
 */
const OrderPaymentModal = ({ visible, order, onClose, onPaymentRegistered }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [paymentsHistory, setPaymentsHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [summary, setSummary] = useState(null);
    const [paymentType, setPaymentType] = useState('TOTAL'); // 'TOTAL' | 'PARCIAL'

    const formatCurrency = (val) => {
        const n = Number(val) || 0;
        return `$ ${n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (date) => {
        if (!date) return '-';
        const d = String(date).split('T')[0];
        const [y, m, day] = d.split('-');
        return `${String(day).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    };

    // Load payment history when modal opens
    useEffect(() => {
        if (visible && order?.id) {
            setLoadingHistory(true);
            getOrderPayments(order.id)
                .then((res) => {
                    if (res?.success) {
                        setSummary(res.data.summary);
                        setPaymentsHistory(res.data.payments || []);
                    }
                })
                .catch(() => {})
                .finally(() => setLoadingHistory(false));
        }
    }, [visible, order?.id]);

    const handleClose = () => {
        form.resetFields();
        setPaymentsHistory([]);
        setSummary(null);
        setPaymentType('TOTAL');
        onClose();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const response = await addOrderPayment(order.id, {
                payment_date: dayjs(values.payment_date).format('YYYY-MM-DD'),
                amount: values.amount,
                payment_method: values.payment_method || null,
                notes: values.notes || null,
            });

            if (response?.success) {
                message.success('Pago registrado exitosamente');
                form.resetFields();
                onPaymentRegistered?.();
                handleClose();
            } else {
                message.error(response?.message || 'Error al registrar el pago');
            }
        } catch (err) {
            if (err?.errorFields) return; // validation failed — antd shows inline errors
            message.error(err?.response?.data?.message || 'Error al registrar el pago');
        } finally {
            setLoading(false);
        }
    };

    // Use fresh summary from API if loaded, fallback to table row data
    const displayTotal   = Number(summary?.order_total   ?? order?.total)       || 0;
    const displayPaid    = Number(summary?.paid_amount    ?? order?.paid_amount) || 0;
    const displayPending = Number(summary?.pending_amount ?? order?.pending_amount
                                                         ?? (displayTotal - displayPaid)) || 0;
    const displayPaymentStatus = summary?.payment_status ?? order?.payment_status ?? 'PENDIENTE';

    // Auto-fill amount when paymentType is TOTAL and pending amount is known
    useEffect(() => {
        if (paymentType === 'TOTAL' && displayPending > 0) {
            form.setFieldValue('amount', parseFloat(displayPending.toFixed(2)));
        }
    }, [paymentType, displayPending]); // eslint-disable-line

    const paymentStatusConfig = {
        PENDIENTE: { color: 'default', label: 'Sin pagar' },
        PARCIAL:   { color: 'purple',  label: 'Parcial'   },
        PAGADO:    { color: 'green',   label: 'Pagado'    },
    };
    const psc = paymentStatusConfig[displayPaymentStatus] || { color: 'default', label: displayPaymentStatus };

    return (
        <Modal
            title={
                <span>
                    <DollarOutlined style={{ marginRight: 8 }} />
                    Registrar Pago — {order?.order_number}
                </span>
            }
            open={visible}
            onCancel={handleClose}
            onOk={handleSubmit}
            okText="Registrar Pago"
            cancelText="Cancelar"
            confirmLoading={loading}
            width={820}
            destroyOnClose
        >
            {/* Order payment summary */}
            <Descriptions size="small" bordered column={3} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Total Orden">
                    <strong>{formatCurrency(displayTotal)}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Pagado">
                    <span style={{ color: '#52c41a' }}>{formatCurrency(displayPaid)}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Pendiente">
                    <span style={{ color: displayPending > 0 ? '#fa8c16' : '#52c41a' }}>
                        {formatCurrency(displayPending)}
                    </span>
                </Descriptions.Item>
            </Descriptions>

            <Tag color={psc.color} style={{ marginBottom: 12 }}>{psc.label}</Tag>

            {displayPaymentStatus !== 'PAGADO' && (
                <Segmented
                    block
                    value={paymentType}
                    onChange={(v) => {
                        setPaymentType(v);
                        if (v === 'TOTAL') {
                            form.setFieldValue('amount', displayPending > 0 ? parseFloat(displayPending.toFixed(2)) : undefined);
                        } else {
                            form.setFieldValue('amount', undefined);
                        }
                    }}
                    options={[
                        { label: '✅ Pago Total', value: 'TOTAL' },
                        { label: '💰 Pago Parcial', value: 'PARCIAL' },
                    ]}
                    style={{ marginBottom: 16 }}
                />
            )}

            {displayPaymentStatus === 'PAGADO' && (
                <Alert
                    type="success"
                    message="Esta orden ya está completamente pagada."
                    showIcon
                    style={{ marginBottom: 12 }}
                />
            )}

            {/* Payment form */}
            <Form form={form} layout="vertical" size="small">
                <Form.Item
                    name="payment_date"
                    label="Fecha del Pago"
                    rules={[{ required: true, message: 'Seleccione la fecha del pago' }]}
                    initialValue={dayjs()}
                >
                    <DatePicker
                        locale={locale}
                        format="DD/MM/YYYY"
                        style={{ width: '100%' }}
                        placeholder="Seleccione fecha"
                    />
                </Form.Item>

                <Form.Item
                    name="amount"
                    label={paymentType === 'TOTAL' ? 'Monto a Pagar (total pendiente)' : 'Monto Parcial'}
                    rules={[
                        { required: true, message: 'Ingrese el monto' },
                        { type: 'number', min: 0.01, message: 'El monto debe ser mayor a cero' },
                    ]}
                    initialValue={displayPending > 0 ? parseFloat(displayPending.toFixed(2)) : undefined}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        min={0.01}
                        precision={2}
                        addonBefore="$"
                        placeholder="0.00"
                        disabled={paymentType === 'TOTAL'}
                    />
                </Form.Item>

                <Form.Item name="payment_method" label="Método de Pago">
                    <Select placeholder="Seleccione método (opcional)" allowClear>
                        <Option value="Efectivo">Efectivo</Option>
                        <Option value="Transferencia Bancaria">Transferencia Bancaria</Option>
                        <Option value="Cheque">Cheque</Option>
                        <Option value="Cuenta Corriente">Cuenta Corriente</Option>
                        <Option value="Otros medios de pago electrónico">Otros medios de pago electrónico</Option>
                    </Select>
                </Form.Item>

                <Form.Item name="notes" label="Nota (opcional)">
                    <TextArea rows={2} maxLength={500} placeholder="Ej: Pago contraentrega — remito #12345" />
                </Form.Item>
            </Form>

            {/* Payment history */}
            {(loadingHistory || paymentsHistory.length > 0) && (
                <>
                    <Divider orientation="left" style={{ margin: '12px 0 8px 0' }}>
                        Historial de Pagos
                    </Divider>
                    {loadingHistory ? (
                        <Spin size="small" />
                    ) : (
                        <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                            {paymentsHistory.map((p) => (
                                <div
                                    key={p.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '4px 0',
                                        borderBottom: '1px solid #f0f0f0',
                                        fontSize: 12,
                                    }}
                                >
                                    <span style={{ color: '#595959' }}>{formatDate(p.payment_date)}</span>
                                    <span>{p.payment_method || '—'}</span>
                                    <span style={{ fontWeight: 600, color: '#52c41a' }}>{formatCurrency(p.amount)}</span>
                                    <span style={{ color: '#8c8c8c', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {p.notes || ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default OrderPaymentModal;
